import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import '../styles/v2/pages/entry-profiles.css';
import {
  Settings, Plus, Edit3, Trash2, Search, X, Copy, Tablet,
  RefreshCw, ChevronRight, CheckCircle, AlertTriangle, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import entryProfilesTutorial from '../components/tutorials/configs/entryProfilesTutorial';

const SECTION_LABELS = {
  oostoConfig: { label: 'OnWatch / FR Settings', color: '#22c55e' },
  rtspConfig: { label: 'RTSP / Camera', color: '#3b82f6' },
  uiConfig: { label: 'UI / Branding', color: '#a855f7' },
  kioskConfig: { label: 'Kiosk Behavior', color: '#f59e0b' },
  securityConfig: { label: 'Security Policy', color: '#ef4444' },
  networkConfig: { label: 'Network', color: '#06b6d4' },
};

const EntryProfiles = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(entryProfilesTutorial);

  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    isDefault: false,
    oostoConfig: '{}',
    rtspConfig: '{}',
    uiConfig: '{}',
    kioskConfig: '{}',
    securityConfig: '{}',
    networkConfig: '{}',
  });

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await fetchWithTenant('/api/entry/profiles');
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (e) {
      console.error('Failed to fetch profiles:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const resetForm = () => {
    setForm({
      name: '', description: '', isDefault: false,
      oostoConfig: '{}', rtspConfig: '{}', uiConfig: '{}',
      kioskConfig: '{}', securityConfig: '{}', networkConfig: '{}',
    });
    setEditingProfile(null);
  };

  const openCreate = () => { resetForm(); setShowModal(true); };

  const openEdit = (profile) => {
    setForm({
      name: profile.name,
      description: profile.description || '',
      isDefault: profile.isDefault,
      oostoConfig: JSON.stringify(profile.oostoConfig || {}, null, 2),
      rtspConfig: JSON.stringify(profile.rtspConfig || {}, null, 2),
      uiConfig: JSON.stringify(profile.uiConfig || {}, null, 2),
      kioskConfig: JSON.stringify(profile.kioskConfig || {}, null, 2),
      securityConfig: JSON.stringify(profile.securityConfig || {}, null, 2),
      networkConfig: JSON.stringify(profile.networkConfig || {}, null, 2),
    });
    setEditingProfile(profile);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name,
        description: form.description || null,
        isDefault: form.isDefault,
      };
      // Parse JSON sections
      for (const key of Object.keys(SECTION_LABELS)) {
        try {
          const parsed = JSON.parse(form[key] || '{}');
          body[key] = Object.keys(parsed).length > 0 ? parsed : null;
        } catch { body[key] = null; }
      }

      const url = editingProfile
        ? '/api/entry/profiles/' + editingProfile.id
        : '/api/entry/profiles';
      const method = editingProfile ? 'PATCH' : 'POST';

      const res = await fetchWithTenant(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setShowModal(false);
        resetForm();
        fetchProfiles();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to save profile');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetchWithTenant('/api/entry/profiles/' + deleteTarget.id, { method: 'DELETE' });
      if (res.ok) {
        setDeleteTarget(null);
        fetchProfiles();
      }
    } catch (e) { console.error(e); }
  };

  const filtered = profiles.filter(p => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return p.name.toLowerCase().includes(s) || (p.description || '').toLowerCase().includes(s);
  });

  const configSections = (profile) => {
    return Object.entries(SECTION_LABELS).filter(([key]) => {
      const val = profile[key];
      return val && Object.keys(val).length > 0;
    }).map(([key, cfg]) => cfg.label);
  };

  if (loading) {
    return <div className="entry-profiles"><div className="entry-loading"><RefreshCw size={24} className="spin" /><span>Loading profiles...</span></div></div>;
  }

  return (
    <div className="entry-profiles">
      <div className="entry-header">
        <div className="entry-header-left">
          <h1><Settings size={24} /> Configuration Profiles</h1>
          <span className="entry-header-sub">Define and manage tablet configuration templates</span>
        </div>
        <div className="entry-header-actions">
          <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> New Profile</button>
          <button className="tutorial-help-btn" onClick={() => startTutorial(entryProfilesTutorial.module, entryProfilesTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      <div className="entry-toolbar">
        <div className="entry-search">
          <Search size={16} />
          <input type="text" placeholder="Search profiles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="entry-empty">
          <Settings size={48} style={{ opacity: 0.3 }} />
          <h3>{profiles.length === 0 ? 'No Profiles Yet' : 'No Matching Profiles'}</h3>
          <p>{profiles.length === 0 ? 'Create a configuration profile to define consistent settings for your tablet fleet.' : 'Try adjusting your search.'}</p>
          {profiles.length === 0 && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Create First Profile</button>
          )}
        </div>
      ) : (
        <div className="entry-profile-grid">
          {filtered.map(profile => (
            <div key={profile.id} className="entry-profile-card">
              <div className="entry-profile-card-header">
                <div>
                  <div className="entry-profile-name">
                    {profile.name}
                    {profile.isDefault && <span className="entry-profile-default-badge">Default</span>}
                  </div>
                  {profile.description && <div className="entry-profile-desc">{profile.description}</div>}
                </div>
                <div className="entry-profile-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(profile)} title="Edit"><Edit3 size={14} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(profile)} title="Delete"><Trash2 size={14} /></button>
                </div>
              </div>

              <div className="entry-profile-sections">
                {configSections(profile).length > 0 ? (
                  configSections(profile).map(label => (
                    <span key={label} className="entry-profile-section-pill">{label}</span>
                  ))
                ) : (
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No configuration sections defined</span>
                )}
              </div>

              <div className="entry-profile-footer">
                <div className="entry-profile-device-count">
                  <Tablet size={14} /> {profile._count?.devices || 0} device{(profile._count?.devices || 0) !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="platform-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="platform-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="platform-modal-header">
              <h3>{editingProfile ? <><Edit3 size={18} /> Edit Profile</> : <><Plus size={18} /> New Profile</>}</h3>
              <button onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <div className="form-group">
                <label>Profile Name *</label>
                <input type="text" className="form-input" placeholder="e.g., Lobby - High Security" value={form.name} onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" className="form-input" placeholder="Optional description" value={form.description} onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))} />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={form.isDefault} onChange={e => setForm(prev => ({ ...prev, isDefault: e.target.checked }))} />
                <label style={{ margin: 0 }}>Set as default profile for new devices</label>
              </div>

              <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '16px' }}>
                <h4 style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Configuration Sections (JSON)</h4>
                {Object.entries(SECTION_LABELS).map(([key, cfg]) => (
                  <div key={key} className="form-group">
                    <label style={{ color: cfg.color }}>{cfg.label}</label>
                    <textarea
                      className="form-input"
                      rows={3}
                      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '12px' }}
                      value={form[key]}
                      onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder="{}"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name.trim()}>
                {saving ? 'Saving...' : editingProfile ? 'Save Changes' : 'Create Profile'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="platform-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="platform-modal delete" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="platform-modal-header">
              <h3><AlertTriangle size={18} className="icon-danger" /> Delete Profile</h3>
              <button onClick={() => setDeleteTarget(null)}><X size={18} /></button>
            </div>
            <div className="platform-modal-body">
              <p className="platform-delete-message">
                This will delete the profile <strong>{deleteTarget.name}</strong> and unassign it from {deleteTarget._count?.devices || 0} device(s). The devices will continue operating with their last applied configuration.
              </p>
            </div>
            <div className="platform-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete}>Delete Profile</button>
            </div>
          </div>
        </div>
      )}

      {showPrompt && (
        <TutorialPrompt
          module={entryProfilesTutorial.module}
          moduleName={entryProfilesTutorial.moduleName}
          steps={entryProfilesTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default EntryProfiles;
